// Просьбы, которые продукт задаёт сам. SMY-121, SMY-174
//
// ⚠️ Файл переписан 09.09.2026 вместе с переходом на почту. Просьб было три,
// осталась одна, и обе ушедшие ушли по одной причине — исчез их предмет.
//
//   1. **Имя** стало ТРЕТЬИМ ШАГОМ РЕГИСТРАЦИИ (SMY-165). Две просьбы об
//      одном и том же — одна шагом, другая всплывающим окном — это
//      гарантированный повтор: пропустивший поле имени получал бы модалку
//      через минуту.
//   2. **Почта для чека** исчезла вместе с состоянием, которое её вызывало
//      («оплатил, а почты нет»). Почта стала логином: у зарегистрированного
//      она есть по определению, а гость не платит вовсе.
//   3. **Пароль** остался, и довод под ним прежний: пароль защищает то, что
//      уже нажито, и человеку без единого результата предлагают защитить
//      пустоту. Но решает теперь не клиент, а лестница на сервере — +3
//      успешных чата после регистрации, потом ещё +3, дальше никогда.
//
// ⚠️ И ОТКАЗЫ БОЛЬШЕ НЕ ЖИВУТ В `localStorage`. Прежнее объяснение — «это не
// свойство человека, а „уже спрашивали на этом устройстве“» — было верным для
// имени и пароля порознь. Оно перестало работать, когда отказ стал двигать
// СТЕНУ (SMY-161): стена, которую снимает очистка данных сайта, стеной не
// является. Локальный слепок остался ровно на один экран — чтобы закрытая
// модалка исчезла немедленно, не дожидаясь ответа `/v1/me`.
//
// ⚠️ ГОСТЬ В ВЕБЕ ПОЯВИЛСЯ 10.09.2026, и прежняя запись здесь говорила
// обратное — «нет и не будет». Довод её был верен и никуда не делся: здесь
// «устройство» это вкладка, и гость в приватном окне — новый гость на каждый
// заход. Снят он не отменой, а ЧИСЛОМ: гостю в кабинете достаётся одна
// задача (`guest.webSignupSteps` = `[1]`), а не половина тарифа. Одна задача
// на приватное окно — цена привлечения; половина тарифа была бы дырой.
//
// Отсюда и предложение зарегистрироваться, которого раньше тут не было:
// показывается на стене, то есть сразу после первого результата.

import { useEffect, useState, type FormEvent } from 'react'
import {
  checkPassword,
  PASSWORD_MIN_LENGTH,
  PASSWORD_OFFER_TEXT,
  PASSWORD_PROBLEM_TEXT,
} from '@smyslika/core'
import { ApiError, OfflineError } from '@smyslika/api-client'

import { useSession } from '../session.tsx'
import { Button, Field, Sheet } from '../ui/index.ts'

/** Общее у обеих модалок: текст ошибки словами, а не кодом. */
const failText = (e: unknown): string => {
  if (e instanceof OfflineError) return 'Нет связи. Проверь интернет и попробуй снова.'
  return e instanceof Error ? e.message : 'Не получилось. Попробуй ещё раз.'
}

function AskPassword({ onClose }: { onClose: () => void }) {
  const { account, me, refreshMe } = useSession()
  const [password, setPassword] = useState('')
  const [shown, setShown] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Правила из ядра, те же, что проверит сервер. Своя проверка здесь была бы
  // вторым мнением о том же — и однажды разошлась бы с сервером, погасив
  // кнопку там, где сервер бы принял.
  // Логин — почта (SMY-167): правило сравнивает имя до собаки.
  const problem = password === '' ? null : checkPassword(password, me?.email)

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await account.setPassword(password)
      await refreshMe()
      onClose()
    } catch (err) {
      // Сервер знает про пароль то, чего не знает форма (например, что он
      // совпал со старым), и его формулировку показываем как есть.
      setError(err instanceof ApiError ? err.message : failText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open onClose={onClose} closable label="Пароль для входа">
      <form onSubmit={(e) => void save(e)}>
        <div className="onboard__head">
          <h2 className="onboard__title">Входить без ожидания письма</h2>
          {/* Текст один на оба окна и обе платформы — из ядра: четыре копии
              одного абзаца расходятся в той, которую правят. */}
          <p className="onboard__explain">{PASSWORD_OFFER_TEXT}</p>
        </div>

        <label className="onboard__label" htmlFor="onboard-password">
          Пароль
        </label>
        <div className="onboard__secret">
          <Field
            id="onboard-password"
            boxy
            autoFocus
            value={password}
            type={shown ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={`Не короче ${PASSWORD_MIN_LENGTH} знаков`}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* Показать вместо второго поля «повторите пароль». Повтор ловит
              опечатку тем, что заставляет напечатать дважды; глаз ловит её
              же, но показывая. И цена ошибки здесь невелика: забытый пароль
              не запирает аккаунт — вход по коду из письма остаётся. */}
          <button
            type="button"
            className="onboard__peek"
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {shown ? 'Скрыть' : 'Показать'}
          </button>
        </div>

        {problem != null ? <p className="onboard__hint">{PASSWORD_PROBLEM_TEXT[problem]}</p> : null}

        {error != null ? (
          <p className="onboard__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="onboard__actions">
          <Button kind="ghost" onClick={onClose}>
            Не сейчас
          </Button>
          <Button type="submit" disabled={busy || password === '' || problem != null}>
            Сохранить пароль
          </Button>
        </div>

        <p className="onboard__foot">Передумаешь — пароль заводится и снимается в профиле.</p>
      </form>
    </Sheet>
  )
}

/**
 * Куда отправить чек. Показывается тому, кто уже заплатил.
 *
 * Окно без выхода: ни крестика, ни «не сейчас», ни Esc, ни клика по фону.
 * `Sheet` зовёт `onClose` и на Esc, и на фон независимо от `closable`,
 * поэтому пустой обработчик здесь — не заглушка «дописать потом», а способ
 * закрыть все три выхода одним местом.
 *
 * Текст объясняет ПРИЧИНУ, а не требует. Человек, у которого только что
 * списались деньги и который упёрся в поле ввода, имеет право знать, зачем
 * оно, — иначе непропускаемое окно читается как сбор контактов под видом
 * покупки.
 */
/**
 * Привязка почты для первой волны. SMY-181
 *
 * ⚠️ ОКНО ВРЕМЕННОЕ и уходит вместе с входом по номеру (SMY-182).
 *
 * Сессия живёт 90 дней и способа входа не касается — человек с открытой
 * вкладкой ничего не заметит, пока не решит войти заново. Тем ценнее момент:
 * просить надо, пока он ВНУТРИ, а не когда уже оказался снаружи и без
 * способа вернуться.
 *
 * Сама привязка — тот же шаг с кодом, что в регистрации, и отдельного экрана
 * под неё нет намеренно: `verifyEmailCode` с живой сессией дописывает почту в
 * ТУ ЖЕ строку (исход `confirmed`, SMY-164). Второй экран означал бы вторую
 * реализацию одного и того же.
 */
function BindEmail({ onClose }: { onClose: () => void }) {
  return (
    <Sheet open onClose={onClose} label="Привязать почту">
      <div className="onboard">
        <h2 className="onboard__title">Привяжите почту</h2>
        <p className="onboard__text">
          Вход по номеру скоро закончится — почта станет единственным способом войти. Это минута, и
          всё, что у вас есть, останется на месте.
        </p>
        <a className="btn btn--primary btn--wide" href="/login">
          Привязать почту
        </a>
        <button type="button" className="btn btn--ghost btn--wide" onClick={onClose}>
          Не сейчас
        </button>
        <p className="onboard__note">Пока номер работает, это можно отложить.</p>
      </div>
    </Sheet>
  )
}

/**
 * Предложение гостю — после первой же сделанной задачи. SMY-175
 *
 * ⚠️ ПОКАЗЫВАЕТСЯ НА СТЕНЕ, А НЕ ПО ОТДЕЛЬНОЙ ПРОСЬБЕ. В кабинете ступени
 * лестницы это `[1]`, а последняя ступень и есть стена — значит `blocked`
 * наступает ровно тогда, когда человек увидел свой первый результат. Второго
 * числа под «когда просить» здесь нет намеренно: разъехавшись, они дали бы
 * стену раньше просьбы, и человек упёрся бы в неё, не услышав, чего от него
 * хотят.
 *
 * ⚠️ ПОСЛЕДНЯЯ СТРОКА — ПРО ПОТЕРЮ, И ОНА ОБЯЗАТЕЛЬНА. Гостевой токен живёт
 * во вкладке и умирает вместе с ней (`session.tsx`). Умолчать об этом значит
 * дать человеку закрыть вкладку в уверенности, что работа где-то осталась.
 *
 * Ведёт на `/login`, как и привязка почты ниже: сама регистрация — тот же
 * экран с почтой и кодом, и второй реализацией он быть не должен. Работа при
 * этом никуда не денется: подтверждение почты дописывает её в ТУ ЖЕ строку,
 * переносить нечего.
 *
 * ⚠️ ИСКЛЮЧЕНИЕ ОДНО — ОКНО ПЕРЕД ОПЛАТОЙ (21.09.2026, SMY-341). Гость у кнопки
 * оплаты заводит аккаунт в окне «Нужен аккаунт» (`purchase.tsx`), а не на
 * `/login`: оттуда кабинет открывается с начала, и выбранный тариф теряется.
 * Общее у них — правила шага почты и кода (`email-code.ts`), и второй
 * реализацией их не пишут. Разное намеренно: у окна нет шага имени — после кода
 * человек идёт платить, а имя спросит профиль.
 */
function AskSignup({ onClose }: { onClose: () => void }) {
  const { events } = useSession()
  // ⚠️ Показ пишется В МОНТИРОВАНИИ, а не в теле отрисовки: в теле он уходил
  // бы на каждый перерисованный кадр, и «показов» в отчёте оказалось бы в
  // десятки раз больше, чем окон. То же правило и в мобильном.
  //
  // Ступень здесь всегда `wall`: в кабинете ступеней ровно одна, и она же
  // стена. Перечислением, а не числом, — числа правятся из конфигурации, и
  // записанное число сделало бы старые события несравнимыми с новыми.
  useEffect(() => {
    events.signupPrompt('wall')
  }, [events])

  return (
    <Sheet open onClose={onClose} label="Сохранить работу">
      <div className="onboard">
        <h2 className="onboard__title">Сохранить результат?</h2>
        <p className="onboard__text">
          Оставьте почту — работа останется в вашем профиле, а лимиты станут больше. Бесплатно и
          навсегда, без карты.
        </p>
        <a
          className="btn btn--primary btn--wide"
          href="/login"
          onClick={() => events.signupPrompt('wall', 'accepted')}
        >
          Ввести почту
        </a>
        <button
          type="button"
          className="btn btn--ghost btn--wide"
          onClick={() => {
            events.signupPrompt('wall', 'skipped')
            onClose()
          }}
        >
          Не сейчас
        </button>
        <p className="onboard__note">
          Без почты работа не сохранится: закроете вкладку — её не будет.
        </p>
      </div>
    </Sheet>
  )
}

export function Onboarding() {
  const { auth, me, refreshMe } = useSession()
  /**
   * Что закрыли ПРЯМО СЕЙЧАС. Живёт до перезагрузки страницы и дальше не
   * нужен: настоящую память держит сервер, а это — чтобы закрытая модалка
   * исчезла немедленно, не дожидаясь ответа `/v1/me`.
   */
  const [closed, setClosed] = useState(false)

  if (me == null) return null

  /**
   * Отказ. `what` называет, КАКУЮ лестницу двигать: счётчики на сервере
   * разные (`signup_asked_count` и `password_asked_count`), и перепутать их
   * значит показать человеку то же самое окно завтра снова.
   */
  const decline = (what: 'signup' | 'password') => (): void => {
    setClosed(true)
    // Отказ уходит на сервер, и без этого запроса модалка вернулась бы на
    // следующей же перезагрузке: счётчик не сдвинулся, а лестница считает
    // по нему.
    void (async () => {
      try {
        if (what === 'signup') await auth.declineSignup()
        else await auth.declinePassword()
        await refreshMe()
      } catch {
        // Не дошло — предложение вернётся позже. Это лучше, чем ронять экран
        // из-за отказа, который человек уже сделал: он-то его закрыл.
      }
    })()
  }

  // ⚠️ ГОСТЬ — ПЕРЕД ВСЕМ ОСТАЛЬНЫМ. У него нет ни номера, ни пароля, и
  // предлагать ему что-либо, кроме почты, попросту нечего: сервер и не
  // присылает ему ни `needsEmail`, ни `passwordOffer`. Порядок здесь на
  // случай, если однажды пришлёт.
  if (me.signup?.blocked === true && !closed) return <AskSignup onClose={decline('signup')} />

  // ⚠️ ПРИВЯЗКА ПОЧТЫ — ПЕРВОЙ И ВНЕ ОЧЕРЕДИ (SMY-181). Человек пришёл по
  // номеру, номер уходит, и предлагать ему пароль поверх этого значит
  // перепутать второй способ входа с единственным: без почты он однажды
  // просто не сможет войти.
  //
  // Закрывается — пока номер жив. После его снятия окно уйдёт вместе с
  // полем `needsEmail` (SMY-182), и закрывать станет нечего.
  if (me.needsEmail && !closed) return <BindEmail onClose={decline('password')} />

  // Показываем по НАЛИЧИЮ поля: тому, кому предлагать нечего — пароль уже
  // есть, ступени кончились, ещё не зарегистрирован, — сервер его не
  // присылает вовсе (SMY-168).
  const offer = me.passwordOffer
  if (offer?.nextAskAt != null && offer.doneChats >= offer.nextAskAt && !closed) {
    return <AskPassword onClose={decline('password')} />
  }
  return null
}
